/**
 * Projects — time-bound PARA containers with an outcome and an end (SPEC.md §4).
 * Self-contained the same way `AreasCard` is; see that file's docstring for why.
 */

import { useCallback, useRef, useState } from "react";
import { ApiError, api } from "../api";
import { queueProjectCreate } from "../taskQueue";
import { useAreasAndProjects } from "../areasProjects";
import { PROJECT_VOLLEY, celebrateProject, useFireworks } from "./fireworksContext";
import { basicTaskActions } from "../taskActions";
import { todayLocalDate } from "./recurrence";
import { EyeToggle } from "./EyeToggle";
import { AddRow, SortHeader, StructureRow, type SortKey } from "./StructureRow";

export function ProjectsCard({
  refreshKey,
  onChange,
}: {
  /** Bumped whenever something elsewhere changed — an area renamed or archived, most
   * directly — so this card's area-name lookups and its own drawers stay current. */
  refreshKey?: number;
  /** Called after this card changes anything, so siblings relying on the same data
   * (Areas, wherever it lives) know to refetch too. */
  onChange?: () => void | Promise<void>;
}) {
  const { areas, projects, error: loadError, reload, loaded } = useAreasAndProjects(refreshKey);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({
    key: "name",
    desc: false,
  });
  const [showArchived, setShowArchived] = useState(false);
  const burst = useFireworks();
  const actions = basicTaskActions(burst);
  const today = todayLocalDate();

  // A project whose own status is "active" is still shelved if its area is (gitea #49,
  // D206) — archiving an area hides its projects too, not just its tasks and notes.
  const areaArchived = (project: (typeof projects)[number]) =>
    areas.find((a) => a.id === project.area_id)?.status === "archived";

  // What was already done or archived *before* this session started, captured once on the
  // first successful load, never recomputed on a later `reload()`. A project that finishes
  // or gets archived during this session isn't in here, so it stays visible, crossed out,
  // through any number of reloads; only an actual page refresh (a fresh mount, a fresh
  // snapshot) makes it disappear (gitea #49, D204).
  const priorStale = useRef<Set<string> | null>(null);
  if (loaded && priorStale.current === null) {
    priorStale.current = new Set(
      projects.filter((p) => p.status !== "active").map((p) => p.id),
    );
  }
  const visibleProjects = projects.filter((project) => {
    if (areaArchived(project)) {
      // No grace period here, unlike a project's own direct archive below — this project
      // was never touched, its area was. The feedback the owner wants from archiving an
      // area is that its whole tree disappears right away (gitea #49, D206), not that a
      // project three clicks away quietly survives until the next refresh.
      return showArchived;
    }
    if (project.status === "active") return true;
    const isPreExisting = priorStale.current?.has(project.id) ?? false;
    if (project.status === "done") return !isPreExisting;
    // archived
    return showArchived || !isPreExisting;
  });

  /** What archiving this project will fold out of view — the confirmation's own
   * enumeration (gitea #49, D206). A project has no children of its own besides tasks and
   * notes; the area-level version of this also counts active child projects. */
  function contentsOf(project: (typeof projects)[number]): string {
    const parts: string[] = [];
    if (project.open_tasks) {
      parts.push(`${project.open_tasks} open task${project.open_tasks === 1 ? "" : "s"}`);
    }
    if (project.notes_count) {
      parts.push(`${project.notes_count} note${project.notes_count === 1 ? "" : "s"}`);
    }
    return parts.join(", ");
  }

  /** Clicking the active column flips it; clicking another switches to it, ascending. */
  const toggleSort = (key: SortKey) =>
    setSort((current) => ({
      key,
      desc: current.key === key ? !current.desc : false,
    }));

  /** `whenOffline` queues instead of failing when the server is unreachable (§8.2, D166). */
  const act = useCallback(
    async (fn: () => Promise<unknown>, whenOffline?: () => Promise<unknown>) => {
      try {
        await fn();
      } catch (err) {
        if (whenOffline && err instanceof ApiError && err.isOffline) {
          await whenOffline();
        } else {
          setError(err instanceof Error ? err.message : String(err));
          return;
        }
      }
      await reload();
      await onChange?.();
    },
    [reload, onChange],
  );

  // No API call of its own — a drawer already made one — just the same reload/onChange
  // tail `act` gives every other mutation here, so a task ticked off inside a row's drawer
  // updates that row's own `· N` count and tells siblings relying on the same data too.
  const notifyDrawerChange = useCallback(async () => {
    await reload();
    await onChange?.();
  }, [reload, onChange]);

  // Sorted here rather than by the server: it is a view preference, and the list is small
  // enough that a round trip to re-order a dozen rows would be silly.
  const areaName = (id: string) => areas.find((a) => a.id === id)?.name ?? "";
  const sortedProjects = [...visibleProjects].sort((a, b) => {
    const compared =
      sort.key === "area"
        ? areaName(a.area_id).localeCompare(areaName(b.area_id)) || a.name.localeCompare(b.name)
        : a.name.localeCompare(b.name);
    return sort.desc ? -compared : compared;
  });

  return (
    <section className="panel">
      <header>
        <h2>Projects</h2>
        <span className="row">
          <span className="muted small">Time-bound. Has an outcome and an end.</span>
          <EyeToggle
            open={showArchived}
            onToggle={() => setShowArchived((v) => !v)}
            label="archived projects"
          />
        </span>
      </header>

      {(error ?? loadError) && (
        <p className="error" style={{ margin: "0 0 0.6rem" }}>
          {error ?? loadError}
        </p>
      )}

      <p className="muted small">
        Tick a project off the way you would a task - it reached its outcome, and it lands in
        your wins. <strong>Archive</strong> is for one that is over <em>without</em> having got
        there, and stays available afterwards for a finished project you also want filed away.
        Both keep every task and every completion; §4.1 makes archiving a state, not a
        deletion.
      </p>

      <div className="structure-wrap">
        <table className="tokens structure" role="table">
          <thead role="rowgroup">
            <tr role="row">
              <SortHeader label="Name" column="name" sort={sort} onSort={toggleSort} />
              <th role="columnheader" className="unsorted">
                Quiet after
              </th>
              <SortHeader label="Area" column="area" sort={sort} onSort={toggleSort} />
              <th role="columnheader" className="spacer" />
            </tr>
          </thead>
          <tbody role="rowgroup">
            {sortedProjects.map((project) => (
              <StructureRow
                key={project.id}
                id={project.id}
                kind="project"
                today={today}
                allAreas={areas}
                allProjects={projects}
                actions={actions}
                drawerRefreshKey={refreshKey}
                onDrawerChange={notifyDrawerChange}
                onDelete={() => act(() => api.deleteProject(project.id))}
                name={project.name}
                threshold={project.stale_after_days}
                taskCount={project.open_tasks}
                context={areas.find((a) => a.id === project.area_id)?.name ?? "-"}
                areaId={project.area_id}
                archived={project.status !== "active"}
                done={project.status === "done"}
                onComplete={() => {
                  if (project.status === "done") {
                    return act(() => api.patchProject(project.id, { status: "active" }));
                  }
                  celebrateProject(burst, PROJECT_VOLLEY);
                  return act(() => api.completeProject(project.id));
                }}
                statusLabel={project.status === "archived" ? "Restore" : undefined}
                statusHint="Bring it back into circulation."
                extraAction={
                  // Archive stays available whatever the state: a project can be finished
                  // and then shelved, and hiding the control the moment it was marked done
                  // meant the only way back was to reopen it first.
                  project.status === "archived"
                    ? undefined
                    : {
                        label: "Archive",
                        hint:
                          project.status === "done"
                            ? "Finished and now filed away."
                            : "Over, but not finished. Same effect, different truth.",
                        run: () => {
                          const contents = contentsOf(project);
                          const detail = contents
                            ? `\n\nIt holds ${contents}, all of which will be hidden from ` +
                              "your other views until you restore it."
                            : "";
                          if (!window.confirm(`Archive "${project.name}"?${detail}`)) return;
                          void act(() =>
                            api.patchProject(project.id, {
                              status: "archived",
                            }),
                          );
                        },
                      }
                }
                onSave={(patch) => act(() => api.patchProject(project.id, patch))}
                onStatus={() =>
                  act(() =>
                    api.patchProject(project.id, {
                      status: project.status === "active" ? "done" : "active",
                    }),
                  )
                }
              />
            ))}
          </tbody>
        </table>
      </div>

      <AddRow
        label="project"
        areas={areas.filter((a) => a.status === "active")}
        onAdd={(name, threshold, areaId) =>
          act(
            () =>
              api.createProject({
                name,
                area_id: areaId!,
                stale_after_days: threshold,
              }),
            () => queueProjectCreate(name, areaId!),
          )
        }
      />
    </section>
  );
}
